import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, ShieldCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

export default function Diagnostics() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = () => api.katlasDiagnostics("full").then(setData).catch(e => setErr(String(e?.message || e)));
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-[900px] mx-auto px-6 py-10 fc-fade-up" data-testid="diagnostics-root">
      <Link to="/sandbox" className="text-[12px] text-[var(--ink-soft)] inline-flex items-center gap-1.5 mb-3 fc-link-underline">
        <ArrowLeft size={12}/> Back to live sandbox
      </Link>
      <div className="fc-eyebrow mb-2">KATLAS diagnostics</div>
      <h1 className="display text-[28px] mb-1">Node configuration and reachability</h1>
      <p className="text-[var(--ink-soft)] text-[13px] mb-6">
        Backend-side probe. No bearer token or auth header is included in this response.
      </p>

      <div className="flex gap-2 mb-6">
        <Button onClick={load} variant="outline" className="rounded-sm h-9" data-testid="rerun-probe">
          <Activity size={13} className="mr-2"/> Re-run probe
        </Button>
      </div>

      {err && <div className="border border-[var(--red)] bg-[#FEF2F2] p-3 text-[13px] text-[var(--red)]">{err}</div>}
      {data && (
        <div className="fc-card p-5 space-y-5" data-testid="diagnostics-payload">
          <Row label="Mode"                    value={<span className="mono">{data.mode}</span>}/>
          <Row label="Node URL set"            value={<Bool v={data.node_url_set}/>}/>
          <Row label="Node URL"                value={<span className="mono break-all">{data.node_url || "—"}</span>}/>
          <Row label="Bearer token set"        value={<Bool v={data.bearer_token_set}/>}/>
          <Row label="Simulation fallback"     value={<Bool v={data.simulation_fallback_allowed} invert/>}/>
          <Row label="Timeout (s)"             value={<span className="mono">{data.timeout_seconds}</span>}/>
          <div className="border-t border-[var(--line)] pt-4">
            <div className="fc-eyebrow mb-2">Status probe</div>
            <pre className="mono text-[11px] bg-[var(--paper)] border border-[var(--line)] p-3 overflow-x-auto" data-testid="status-probe">
{JSON.stringify(data.status_probe || {}, null, 2)}
            </pre>
            <div className="text-[11px] text-[var(--ink-soft)] mt-2 flex items-center gap-1.5">
              <ShieldCheck size={11} className="text-[var(--teal)]"/>
              Bearer token never appears in the response.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-[180px_1fr] gap-4 text-[13px]">
      <div className="fc-eyebrow pt-0.5">{label}</div>
      <div className="min-w-0">{value}</div>
    </div>
  );
}

function Bool({ v, invert }) {
  const ok = invert ? !v : v;
  return (
    <span className={`mono text-[12px] ${ok ? "text-[var(--green)]" : "text-[var(--amber)]"}`}>
      {String(v)}
    </span>
  );
}
